import { useState, useEffect, useRef, useCallback } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { HelpCircle } from 'lucide-preact'

interface InfoPopoverProps {
  infoText: string
  className?: string
  size?: number
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto'
  width?: string
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

type PlacementType = 'top' | 'bottom' | 'left' | 'right'

interface AvailableSpace {
  top: number
  bottom: number
  left: number
  right: number
}

const calculateAvailableSpace = (triggerRect: DOMRect): AvailableSpace => {
  return {
    top: triggerRect.top,
    bottom: window.innerHeight - triggerRect.bottom,
    left: triggerRect.left,
    right: window.innerWidth - triggerRect.right,
  }
}

const findBestPlacement = (
  availableSpace: AvailableSpace,
  tooltipWidth: number,
  tooltipHeight: number,
  preferredPlacement: PlacementType | 'auto'
): PlacementType => {
  const minSpace = 20 // Minimum space required from edge

  const placements: PlacementType[] = ['bottom', 'top', 'right', 'left']

  // If specific placement is requested, try it first
  if (preferredPlacement !== 'auto') {
    const placement = preferredPlacement as PlacementType
    const hasSpace =
      (placement === 'top' && availableSpace.top >= tooltipHeight + minSpace) ||
      (placement === 'bottom' &&
        availableSpace.bottom >= tooltipHeight + minSpace) ||
      (placement === 'left' &&
        availableSpace.left >= tooltipWidth + minSpace) ||
      (placement === 'right' && availableSpace.right >= tooltipWidth + minSpace)

    if (hasSpace) return placement
  }

  // Find placement with most space
  const spaceMap = {
    top: availableSpace.top,
    bottom: availableSpace.bottom,
    left: availableSpace.left,
    right: availableSpace.right,
  }

  return placements.reduce((best, current) =>
    spaceMap[current] > spaceMap[best] ? current : best
  )
}

const useSmartPosition = (
  tooltipRef: React.RefObject<HTMLDivElement>,
  triggerRef: React.RefObject<HTMLButtonElement>,
  placement: PlacementType | 'auto' = 'auto',
  showTooltip: boolean
) => {
  const [calculatedPlacement, setCalculatedPlacement] =
    useState<PlacementType>('bottom')
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current || !showTooltip) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const availableSpace = calculateAvailableSpace(triggerRect)

    const bestPlacement = findBestPlacement(
      availableSpace,
      tooltipRect.width,
      tooltipRect.height,
      placement
    )

    setCalculatedPlacement(bestPlacement)

    // Calculate position based on placement
    let top = 0
    let left = 0

    const triggerCenterX = triggerRect.left + triggerRect.width / 2
    const triggerCenterY = triggerRect.top + triggerRect.height / 2

    switch (bestPlacement) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - 8
        left = triggerCenterX - tooltipRect.width / 2
        break
      case 'bottom':
        top = triggerRect.bottom + 8
        left = triggerCenterX - tooltipRect.width / 2
        break
      case 'left':
        top = triggerCenterY - tooltipRect.height / 2
        left = triggerRect.left - tooltipRect.width - 8
        break
      case 'right':
        top = triggerCenterY - tooltipRect.height / 2
        left = triggerRect.right + 8
        break
    }

    // Ensure tooltip stays within viewport
    const padding = 8
    left = Math.max(
      padding,
      Math.min(left, window.innerWidth - tooltipRect.width - padding)
    )
    top = Math.max(
      padding,
      Math.min(top, window.innerHeight - tooltipRect.height - padding)
    )

    setPosition({ top, left })
  }, [placement, showTooltip])

  useEffect(() => {
    // Small delay to ensure tooltip is rendered; always return a cleanup function
    let timer: number | undefined
    if (showTooltip) {
      timer = window.setTimeout(updatePosition, 5)
    }
    return () => {
      if (timer) window.clearTimeout(timer)
    }
  }, [updatePosition, showTooltip])

  useEffect(() => {
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [updatePosition])

  return { calculatedPlacement, position }
}

export function InfoPopover({
  infoText,
  className = '',
  size = 16,
  placement = 'auto',
  width = 'w-[250px] sm:w-[300px]',
  isOpen,
  onOpenChange,
}: InfoPopoverProps) {
  const [internalShowTooltip, setInternalShowTooltip] = useState(false)
  const showTooltip = isOpen ?? internalShowTooltip

  const tooltipRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { position } = useSmartPosition(
    tooltipRef,
    triggerRef,
    placement,
    showTooltip
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        if (onOpenChange) {
          onOpenChange(false)
        } else {
          setInternalShowTooltip(false)
        }
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip, onOpenChange])

  const tooltipElement = (
    <div
      ref={tooltipRef}
      className={`
        fixed z-50
        ${width}
        max-w-[calc(100vw-16px)]
        p-3
        bg-white 
        border 
        border-gray-300 
        rounded-lg 
        shadow-lg 
        text-sm 
        text-gray-700 
        break-words
      `}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {infoText}
    </div>
  )

  const portal =
    typeof document !== 'undefined' && document.body
      ? createPortal(tooltipElement, document.body)
      : tooltipElement

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type='button'
        className={`transition-colors duration-200 ${
          showTooltip
            ? 'text-cencert-navy-blue-light'
            : 'text-cencert-navy-blue-light hover:text-cencert-navy-blue'
        }`}
        aria-label='Information'
        onClick={() => {
          const newState = !showTooltip
          if (onOpenChange) {
            onOpenChange(newState)
          } else {
            setInternalShowTooltip(newState)
          }
        }}
      >
        <HelpCircle size={size} />
      </button>
      {showTooltip && portal}
    </div>
  )
}
